import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
} from "recharts";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { format } from "date-fns";

type Props = {
  data: any[];
  unitLabel: string;
  flowLabels: { flow1: string; flow2: string; flow3?: string };
  title?: string;
};

/* ---------------- Legend (outside chart to save space) ---------------- */

const LegendItem: React.FC<{ color: string; label: string }> = ({
  color,
  label,
}) => (
  <Box display="flex" alignItems="center" gap={0.75}>
    <span
      style={{
        width: 12,
        height: 12,
        background: color,
        display: "inline-block",
        borderRadius: 2,
      }}
    />
    <Typography variant="caption">{label}</Typography>
  </Box>
);

const LegendRow: React.FC<{
  flowLabels: Props["flowLabels"];
  wrap?: boolean;
}> = ({ flowLabels, wrap = false }) => (
  <Box
    display="flex"
    justifyContent="center"
    gap={3}
    mt={1}
    sx={{
      flexWrap: wrap ? "wrap" : "nowrap",
      rowGap: wrap ? 1 : 0,
    }}
  >
    <LegendItem color="#000000" label={flowLabels.flow1} />
    <LegendItem color="#FFD700" label={flowLabels.flow2} />
    {flowLabels.flow3 && (
      <LegendItem color="#00BFFF" label={flowLabels.flow3} />
    )}
  </Box>
);

/* --------------------- Tick renderers (responsive) --------------------- */

const renderTimeTickDesktop = ({ x, y, payload }: any) => {
  const raw = payload?.value;
  const d =
    typeof raw === "number"
      ? new Date(raw)
      : new Date(String(raw).replace(" ", "T"));
  const label = format(d, "MMM d, HH:00");

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={18}
        transform="rotate(-45)"
        textAnchor="end"
        fill="#666"
        fontSize={12}
      >
        {label}
      </text>
    </g>
  );
};

const renderTimeTickMobile = ({ x, y, payload }: any) => {
  const raw = payload?.value;
  const d =
    typeof raw === "number"
      ? new Date(raw)
      : new Date(String(raw).replace(" ", "T"));
  const label = format(d, "MM/dd");

  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={12} textAnchor="middle" fill="#666" fontSize={11}>
        {label}
      </text>
    </g>
  );
};

/* ------------------------------ Component ------------------------------ */

export const BlenderFlowChart = ({
  data,
  unitLabel,
  flowLabels,
  title = "Blender Material Usage",
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Defensive copy to avoid mutating parent array; also ensures stable reference
  const rows = useMemo(() => (Array.isArray(data) ? [...data] : []), [data]);

  // Responsive sizes/margins similar to other charts
  const height = isMobile ? 260 : 360;
  const margin = isMobile
    ? { top: 6, right: 12, bottom: 26, left: 8 }
    : { top: 5, right: 20, bottom: 56, left: 80 };

  // Axis + bar spacing tweaks for small screens
  const xTick = isMobile ? renderTimeTickMobile : renderTimeTickDesktop;
  const xHeight = isMobile ? 22 : 70;
  const xTickMargin = isMobile ? 6 : 12;

  const yLabel = isMobile
    ? undefined
    : {
        value: `Flow (${unitLabel})`,
        angle: 0 as const,
        position: "insideLeft" as const,
        offset: -40,
        style: { textAnchor: "middle" as const },
      };

  const barCategoryGap = isMobile ? "20%" : "12%"; // slightly wider on mobile
  const barGap = isMobile ? 4 : 6;

  return (
    <>
      <Typography variant={isMobile ? "subtitle1" : "h6"} align="center">
        {title}
      </Typography>

      <Box
        sx={{ width: "100%", minWidth: 280 }}
        display="flex"
        justifyContent="center"
      >
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={rows}
            margin={margin}
            barCategoryGap={barCategoryGap}
            barGap={barGap}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="timestamp"
              tick={xTick}
              height={xHeight}
              interval="preserveEnd"
              minTickGap={isMobile ? 8 : 12}
              tickMargin={xTickMargin}
            />
            <YAxis tickMargin={isMobile ? 6 : 8} label={yLabel} />
            <Tooltip
              labelFormatter={(val: any) =>
                format(
                  typeof val === "number"
                    ? new Date(val)
                    : new Date(String(val).replace(" ", "T")),
                  "MMM d, HH:mm"
                )
              }
            />
            {/* Series colors unchanged */}
            <Bar dataKey="flow1" fill="#000000" name={flowLabels.flow1} />
            <Bar dataKey="flow2" fill="#FFD700" name={flowLabels.flow2} />
            {flowLabels.flow3 && (
              <Bar dataKey="flow3" fill="#00BFFF" name={flowLabels.flow3} />
            )}
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Legend directly under the plot; wraps on mobile */}
      <LegendRow flowLabels={flowLabels} wrap={isMobile} />

      <Box display="flex" justifyContent="center" mt={0.5}>
        <Typography variant="caption">Time</Typography>
      </Box>
    </>
  );
};
